import { get } from 'lodash-es'
import { findEpiSuiteGroupById } from '@/components/combined-predict/models/epiSuite/epiSuiteColumnGroups'
import { getPredictionDataBySource } from '@/components/combined-predict/models/epiSuite/utils'
import { getTitle, getSummaryValue, processPredictionData, buildDisplayPairRow, formatModelValue } from '../utils'

const baseHeaders = ['Mass Amount (%)', 'Half Life (Hours)']

const buildCompartmentRow = ({ model, child, index, getLastValue }) => {
  const modelValue = get(model, child?.name)

  return [
    child?.name,
    formatModelValue(modelValue?.MassAmount),
    formatModelValue(modelValue?.HalfLife),
    formatModelValue(getLastValue({ model, modelValue, index })),
  ]
}

const summaryMapper = (item, meta) => {
  const model = item?.model
  const advectionTimesArray = model?.AdvectionTimesArray ?? []
  const children = meta?.children ?? []

  const summaryValues = children.map((child) => {
    const value = getPredictionDataBySource(item, child.id)
    return getSummaryValue({ value: processPredictionData({ value })?.value, unit: child?.unit }, child?.name)
  })

  const summaryTableRows = children.map((child, index) =>
    buildCompartmentRow({
      model,
      child,
      index,
      getLastValue: () => advectionTimesArray?.[index],
    }),
  )

  // named indices (EPI Suite ordering)
  const persistenceIndex = 0
  const reactionIndex = 1
  const advectionIndex = 2

  const footer = [
    buildDisplayPairRow('Persistence Time', {
      value: model?.aTimes?.[persistenceIndex] ?? model?.Persistence,
      unit: 'hours',
    }),
    buildDisplayPairRow('Reaction Time', { value: model?.aTimes?.[reactionIndex], unit: 'hours' }),
    buildDisplayPairRow('Advection Time', { value: model?.aTimes?.[advectionIndex], unit: 'hours' }),
    buildDisplayPairRow('Percent Reaction', { value: model?.aSums?.[reactionIndex], unit: '%' }),
    buildDisplayPairRow('Percent Advection', { value: model?.aSums?.[advectionIndex], unit: '%' }),
  ]

  return {
    values: summaryValues,
    tables: [
      {
        headers: ['', ...baseHeaders, 'Advection Time (Hours)'],
        rows: summaryTableRows,
        footer,
      },
    ],
  }
}

const descriptorsMapper = (item, meta) => {
  const model = item?.model
  const children = meta?.children ?? []
  if (!model) return { tables: null }

  const descriptorTables = [
    {
      title: 'First Set',
      headers: ['Compartment', ...baseHeaders, 'Emissions (Kg/Hour)'],
      getRows: (model, children) =>
        children.map((child, index) =>
          buildCompartmentRow({
            model,
            child,
            index,
            getLastValue: ({ modelValue }) => modelValue?.Emissions,
          }),
        ),
    },

    {
      title: 'Second Set',
      headers: [
        'Compartment',
        'Fugacity (atm)',
        'Reaction (kg/hr)',
        'Advection (kg/hr)',
        'Reaction (%)',
        'Advection (%)',
      ],
      getRows: (model, children) =>
        children.map((child, index) => [
          child?.name,
          formatModelValue(model?.aFugacities?.[index]),
          formatModelValue(model?.aReaction?.[index]),
          formatModelValue(model?.aAdvection?.[index]),
          formatModelValue(model?.aReactionPercent?.[index]),
          formatModelValue(model?.aAdvectionPercent?.[index]),
        ]),
    },
  ]

  return {
    tables: descriptorTables.map((table) => ({
      title: table.title,
      headers: table.headers,
      rows: table.getRows(model, children),
    })),
  }
}

const fugacity = (items) => {
  const item = items?.find((item) => item?.property === 'fugacityModel')
  const meta = findEpiSuiteGroupById('fugacityModel')

  return {
    title: getTitle(meta),
    description: 'Level III Fugacity Model',
    helpPage: meta?.helpPage,
    summary: summaryMapper(item, meta),
    modelDescriptors: descriptorsMapper(item, meta),
    modelOutput: item?.output ?? null,
  }
}

export { fugacity }
